import { api } from '../../../api/client';
import type {
  AnalysisRowInput, ContactOption, ItemChargeCandidate, ItemRowInput, PriceCodeCandidate,
  ProjectOption, QuoteAnalysisRow, QuoteDetail, QuoteInput, QuoteItemRow, QuoteLookups, QuoteTotals,
  QuoteValidateInput, QuoteValidateResult, NamedLookupKind,
} from './detailTypes';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes — API calls for the Open Quote DETAIL view.
// Backend: `Api/Controllers/V1/Quotes/QuotesController.cs`.
// The shared axios client injects the JWT and drives the global loader (§18.6).
// ─────────────────────────────────────────────────────────────────────────────

interface Envelope<T> { success: boolean; data: T }

export interface QuoteBundle {
  header: QuoteDetail;
  rows: QuoteAnalysisRow[];
  items: QuoteItemRow[];
}

/** One quote with its Analysis Pricing and Item Charge rows. */
export async function fetchQuote(id: string): Promise<QuoteBundle> {
  const res = await api.get<Envelope<QuoteBundle>>(`/api/v1/quotes/${id}`);
  return res.data.data;
}

/** Every header dropdown that does not depend on another field. `prospectId`
 *  keeps the quote's own prospect in the list after it stops being open. */
export async function fetchQuoteLookups(
  prospectId?: string | null, clientId?: string | null,
): Promise<QuoteLookups> {
  const res = await api.get<Envelope<QuoteLookups>>('/api/v1/quotes/lookups', {
    params: { ...(prospectId ? { prospectId } : {}), ...(clientId ? { clientId } : {}) },
  });
  return res.data.data;
}

/** New beside Payment Method / Credit / Source Opportunity. Returns the new id. */
export async function createNamedLookup(kind: NamedLookupKind, name: string): Promise<string> {
  const res = await api.post<Envelope<{ id: string; name: string }>>(`/api/v1/quotes/lookups/${kind}`, { name });
  return res.data.data.id;
}

/** Edit beside Payment Method / Credit / Source Opportunity — renames the row. */
export async function renameNamedLookup(kind: NamedLookupKind, id: string, name: string): Promise<string> {
  const res = await api.put<Envelope<{ id: string; name: string }>>(`/api/v1/quotes/lookups/${kind}/${id}`, { name });
  return res.data.data.id;
}

/** One turnaround the TAT cell picker offers, with what it would bring. */
export interface TatOption { tatId: string; tat: string | null; priority: string | null; priceCode: string | null; price: number }

/** The TAT cell picker — the turnarounds on the row's test's own price list. */
export async function fetchTatOptions(testId: string, isGroup: boolean, componentId: string | null): Promise<TatOption[]> {
  const res = await api.get<Envelope<TatOption[]>>('/api/v1/quotes/tat-options', {
    params: { testId, isGroup, ...(componentId ? { componentId } : {}) },
  });
  return res.data.data ?? [];
}

/** Validate — mark a quote that is Pending Validation as Won or Lost. Returns
 *  the stored outcome and the legacy success message. */
export async function validateQuote(
  id: string, input: QuoteValidateInput,
): Promise<{ result: QuoteValidateResult; message: string }> {
  const res = await api.post<Envelope<QuoteValidateResult> & { message: string }>(
    `/api/v1/quotes/${id}/validate`, input);
  return { result: res.data.data, message: res.data.message };
}

/** The projects of one client. A prospect quote has none. */
export async function fetchProjects(clientId: string): Promise<ProjectOption[]> {
  const res = await api.get<Envelope<ProjectOption[]>>('/api/v1/quotes/lookups/projects', {
    params: { clientId },
  });
  return res.data.data ?? [];
}

/** A client's contacts, or a prospect's single primary contact. */
export async function fetchContacts(
  clientId: string | null, prospectId: string | null,
): Promise<ContactOption[]> {
  const res = await api.get<Envelope<ContactOption[]>>('/api/v1/quotes/lookups/contacts', {
    params: { ...(clientId ? { clientId } : {}), ...(prospectId ? { prospectId } : {}) },
  });
  return res.data.data ?? [];
}

/** The "add price code" popup grid for the chosen sample matrices. */
export async function fetchPriceCodeCandidates(sampleMatrixIds: string[]): Promise<PriceCodeCandidate[]> {
  if (sampleMatrixIds.length === 0) return [];
  const res = await api.get<Envelope<PriceCodeCandidate[]>>('/api/v1/quotes/price-code-candidates', {
    params: { sampleMatrixIds: sampleMatrixIds.join(';') },
  });
  return res.data.data ?? [];
}

/** The "add item charge" popup grid. */
export async function fetchItemChargeCandidates(): Promise<ItemChargeCandidate[]> {
  const res = await api.get<Envelope<ItemChargeCandidate[]>>('/api/v1/quotes/item-charge-candidates');
  return res.data.data ?? [];
}

export interface PricedDraft {
  rows: QuoteAnalysisRow[];
  items: QuoteItemRow[];
  totals: QuoteTotals;
}

/**
 * Price a draft. The server runs the SAME code as the save, so the numbers
 * watched while editing are the numbers that will be stored — no price is ever
 * computed in the browser (CLAUDE.md §1).
 *
 * `silentLoading`: this fires on every cell edit; the blocking loader would
 * flash on each keystroke-commit.
 */
export async function calculateQuote(body: {
  quoteId: string | null;
  rows: AnalysisRowInput[];
  items: ItemRowInput[];
  globalDiscount?: number;
  globalDiscountAmount?: number;
}): Promise<PricedDraft> {
  const res = await api.post<Envelope<PricedDraft>>(
    '/api/v1/quotes/calculate', body, { silentLoading: true } as never);
  return res.data.data;
}

export interface QuoteSaved { oid: string; quoteID: string }

export async function createQuote(input: QuoteInput): Promise<QuoteSaved> {
  const res = await api.post<Envelope<QuoteSaved>>('/api/v1/quotes', input);
  return res.data.data;
}

export async function updateQuote(id: string, input: QuoteInput): Promise<QuoteSaved> {
  const res = await api.put<Envelope<QuoteSaved>>(`/api/v1/quotes/${id}`, input);
  return res.data.data;
}

/** Delete a SET of quotes — all-or-nothing on the server. */
export async function deleteQuotes(ids: string[]): Promise<{ deleted: number; requested: number }> {
  const res = await api.post<Envelope<{ deleted: number; requested: number }>>(
    '/api/v1/quotes/delete', { ids });
  return res.data.data;
}

/** Reactivate a SET of quotes (Canceled / Expired Quotes ▸ Reactive). Each returns
 *  to Pending Submission (Open Quotes). The reason is required. `fromExpired` is the
 *  Expired-list variant: the cancellation is left untouched and a quote still past
 *  its expiration date is refused by the server. */
export async function reactivateQuotes(
  ids: string[], reason: string, fromExpired = false,
): Promise<{ reactivated: number; requested: number }> {
  const res = await api.post<Envelope<{ reactivated: number; requested: number }>>(
    '/api/v1/quotes/reactivate', { ids, reason, fromExpired });
  return res.data.data;
}

/**
 * The quote as a PDF — Open Quotes ▸ Preview. Legacy streams the report as
 * `yyyyMMddHHmmss.pdf` and the browser downloads it; the server stamps that
 * same name in Content-Disposition, so it is taken from there.
 *
 * Returns the filename so the caller can acknowledge the download.
 */
export async function downloadQuoteReport(id: string): Promise<string> {
  const res = await api.get<Blob>(`/api/v1/quotes/${id}/report`, { responseType: 'blob' });
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });

  const disposition = String(res.headers?.['content-disposition'] ?? '');
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ? decodeURIComponent(match[1].trim()) : 'quote.pdf';

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked later: revoking at once can cancel the download in some browsers
  // before they have read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return filename;
}

/**
 * A refused report comes back as JSON inside a Blob (the request asked for a
 * blob), so the server's wording has to be read out of it.
 */
export async function reportErrorMessage(e: unknown): Promise<string | undefined> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return undefined;
  try {
    return (JSON.parse(await data.text()) as { error?: { message?: string } })?.error?.message;
  } catch {
    return undefined;
  }
}
